'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Input,
  InputNumber,
  Tag,
  Modal,
  Tabs,
  Row,
  Col,
  Table,
  Tooltip,
  Select,
  Divider,
  Checkbox,
  Statistic,
  Badge,
  Radio,
  Empty,
} from 'antd';
import { toast } from 'react-toastify';
import {
  AccountBookOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  PlusOutlined,
  SendOutlined,
  DollarOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  PrinterOutlined,
  AuditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/components/AuthContext';
import { RoleName } from '@prisma/client';
import { formatRupiah, rupiahInputFormatter, rupiahInputParser } from '@/lib/formatters';
import { DigitalSignatureBox } from '@/components/DigitalSignature';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface CompletedPOItem {
  id: string;
  poNumber: string;
  date: string;
  supplier: { id: string; name: string; code: string };
  bankAccountSnapshot: { bankName: string; accountNumber: string; accountHolderName: string };
  totalAmount: number;
  totalPaid: number;
  pendingAmount: number;
  outstandingBalance: number;
  availableToRequest: number;
  paymentStatus: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  itemsCount: number;
  threeWayMatch: {
    totalOrdered: number;
    totalGoodReceived: number;
    totalRejectedAtGate: number;
    isFullyDelivered: boolean;
    hasQualityRejections: boolean;
  };
  goodsReceipts: any[];
  paymentRequests: any[];
  paymentAllocations: any[];
}

interface PaymentRequestItem {
  id: string;
  requestNumber: string;
  poId: string;
  po: { poNumber: string; total: number; supplier: { name: string; code: string }; bankAccountSnapshot: any };
  vendorInvoiceNo: string;
  vendorInvoiceDate?: string;
  dueDate?: string;
  requestedAmount: number;
  trancheTitle: string;
  notes?: string;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'PAID';
  requestedBy: { name: string };
  reviewedBy?: { name: string };
  rejectionReason?: string;
  createdAt: string;
  voucher?: { voucherNumber: string };
}

interface PaymentVoucherItem {
  id: string;
  voucherNumber: string;
  paymentDate: string;
  supplier: { name: string; code: string };
  bankAccountSnapshot: any;
  sourceBank: string;
  totalAmount: number;
  transferRef?: string;
  notes?: string;
  disbursedBy: { name: string };
  paymentRequests?: any[];
  allocations: Array<{
    id: string;
    amount: number;
    notes?: string;
    po: { poNumber: string; total: number };
  }>;
}

export default function FinanceWorkspacePage() {
  const { user, hasRole, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('pos');
  const [loading, setLoading] = useState(true);

  // Data states
  const [completedPOs, setCompletedPOs] = useState<CompletedPOItem[]>([]);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequestItem[]>([]);
  const [queueItems, setQueueItems] = useState<any[]>([]);
  const [vouchers, setVouchers] = useState<PaymentVoucherItem[]>([]);
  const [poFilter, setPoFilter] = useState<'UNPAID' | 'ALL'>('UNPAID');
  const [approvalFilter, setApprovalFilter] = useState<'PENDING' | 'ALL'>('PENDING');

  // Create Tranche Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedPOForTranche, setSelectedPOForTranche] = useState<CompletedPOItem | null>(null);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [trancheTitle, setTrancheTitle] = useState('Termin 1 (50%)');
  const [requestedAmount, setRequestedAmount] = useState<number>(0);
  const [trancheNotes, setTrancheNotes] = useState('');
  const [submittingTranche, setSubmittingTranche] = useState(false);

  // Manager Reject Modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState<PaymentRequestItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);

  // Queue Checklist & Disburse Voucher Modal state
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [disburseModalOpen, setDisburseModalOpen] = useState(false);
  const [sourceBank, setSourceBank] = useState('BCA PT XYZ Operasional (883-019-2831)');
  const [transferRef, setTransferRef] = useState('');
  const [disburseNotes, setDisburseNotes] = useState('');
  const [submittingDisburse, setSubmittingDisburse] = useState(false);

  // Voucher Detail / Printable View Modal state
  const [voucherDetailModalOpen, setVoucherDetailModalOpen] = useState(false);
  const [selectedVoucherDetail, setSelectedVoucherDetail] = useState<PaymentVoucherItem | null>(null);

  // 3-Way Match detail modal
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [selectedMatchPO, setSelectedMatchPO] = useState<CompletedPOItem | null>(null);

  // Load all finance datasets
  const fetchAllFinanceData = useCallback(async () => {
    try {
      setLoading(true);
      const [posRes, reqsRes, queueRes, vouchersRes] = await Promise.all([
        fetch('/api/finance/completed-pos'),
        fetch('/api/finance/payment-requests'),
        fetch('/api/finance/queue'),
        fetch('/api/finance/vouchers'),
      ]);

      const [posData, reqsData, queueData, vouchersData] = await Promise.all([
        posRes.json(),
        reqsRes.json(),
        queueRes.json(),
        vouchersRes.json(),
      ]);

      if (posData.success) setCompletedPOs(posData.data || []);
      if (reqsData.success) setPaymentRequests(reqsData.data || []);
      if (queueData.success) setQueueItems(queueData.data || []);
      if (vouchersData.success) setVouchers(vouchersData.data || []);
    } catch (err: unknown) {
      toast.error('Gagal menyinkronkan data keuangan dari server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllFinanceData();
  }, [fetchAllFinanceData]);

  // Derived KPI metrics
  const unpaidPOs = useMemo(
    () => completedPOs.filter((p) => p.outstandingBalance > 0),
    [completedPOs]
  );
  const filteredPOs = useMemo(() => {
    if (poFilter === 'UNPAID') return unpaidPOs;
    return completedPOs;
  }, [poFilter, unpaidPOs, completedPOs]);
  const totalOutstandingVolume = useMemo(
    () => completedPOs.reduce((sum, p) => sum + p.outstandingBalance, 0),
    [completedPOs]
  );
  const totalPayableVolume = useMemo(
    () => completedPOs.reduce((sum, p) => sum + p.totalAmount, 0),
    [completedPOs]
  );
  const totalPaidVolume = useMemo(
    () => completedPOs.reduce((sum, p) => sum + p.totalPaid, 0),
    [completedPOs]
  );
  const pendingManagerApprovalCount = useMemo(
    () => paymentRequests.filter((r) => r.status === 'PENDING_APPROVAL').length,
    [paymentRequests]
  );
  const pendingRequests = useMemo(
    () => paymentRequests.filter((r) => r.status === 'PENDING_APPROVAL'),
    [paymentRequests]
  );
  const filteredRequests = useMemo(() => {
    if (approvalFilter === 'PENDING') return pendingRequests;
    return paymentRequests;
  }, [approvalFilter, pendingRequests, paymentRequests]);
  const queueDisbursementTotal = useMemo(
    () => queueItems.reduce((sum, q) => sum + Number(q.requestedAmount), 0),
    [queueItems]
  );

  // Handlers for Finance Admin: Create Tranche
  const handleOpenCreateTranche = (po: CompletedPOItem) => {
    setSelectedPOForTranche(po);
    setInvoiceNo('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setDueDate(new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]);
    setTrancheTitle(po.totalPaid > 0 ? `Termin Pelunasan (Sisa)` : 'Termin 1 (50%)');
    setRequestedAmount(po.totalPaid > 0 ? po.availableToRequest : Math.round(po.availableToRequest * 0.5));
    setTrancheNotes('');
    setCreateModalOpen(true);
  };

  const handleQuickPercent = (percent: number) => {
    if (!selectedPOForTranche) return;
    const calc = Math.round((selectedPOForTranche.availableToRequest * percent) / 100);
    setRequestedAmount(calc);
    setTrancheTitle(`Termin (${percent}%)`);
  };

  const handleSubmitTranche = async () => {
    if (!selectedPOForTranche) return;
    if (!invoiceNo.trim()) {
      toast.error('Nomor invoice vendor wajib diisi');
      return;
    }
    if (requestedAmount <= 0) {
      toast.error('Nominal pengajuan harus lebih besar dari 0');
      return;
    }
    if (requestedAmount > selectedPOForTranche.availableToRequest) {
      toast.error(`Nominal tidak boleh melebihi sisa saldo ${formatRupiah(selectedPOForTranche.availableToRequest)}`);
      return;
    }

    try {
      setSubmittingTranche(true);
      const res = await fetch('/api/finance/payment-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poId: selectedPOForTranche.id,
          vendorInvoiceNo: invoiceNo,
          vendorInvoiceDate: invoiceDate || undefined,
          dueDate: dueDate || undefined,
          requestedAmount,
          trancheTitle,
          notes: trancheNotes,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal mengajukan termin pembayaran');
      }

      toast.success(json.message);
      setCreateModalOpen(false);
      fetchAllFinanceData();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Terjadi kesalahan sistem');
    } finally {
      setSubmittingTranche(false);
    }
  };

  // Handlers for Finance Manager: Approve & Reject
  const handleApproveRequest = async (requestId: string) => {
    try {
      const res = await fetch(`/api/finance/payment-requests/${requestId}/approve`, {
        method: 'POST',
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menyetujui pengajuan');
      }
      toast.success(json.message);
      fetchAllFinanceData();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menyetujui');
    }
  };

  const handleOpenRejectModal = (item: PaymentRequestItem) => {
    setRequestToReject(item);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleSubmitReject = async () => {
    if (!requestToReject) return;
    if (!rejectReason.trim()) {
      toast.error('Alasan penolakan wajib diisi');
      return;
    }

    try {
      setSubmittingReject(true);
      const res = await fetch(`/api/finance/payment-requests/${requestToReject.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menolak pengajuan');
      }
      toast.success(json.message);
      setRejectModalOpen(false);
      fetchAllFinanceData();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menolak');
    } finally {
      setSubmittingReject(false);
    }
  };

  // Handlers for Finance Admin: Disburse Multi-PO Payment Voucher
  const selectedQueueDetails = useMemo(
    () => queueItems.filter((q) => selectedQueueIds.includes(q.id)),
    [queueItems, selectedQueueIds]
  );

  const selectedTransferTotal = useMemo(
    () => selectedQueueDetails.reduce((sum, q) => sum + Number(q.requestedAmount), 0),
    [selectedQueueDetails]
  );

  // Group validation: check if all selected queue items have same supplier & bank
  const canConsolidateSelection = useMemo(() => {
    if (selectedQueueDetails.length <= 1) return true;
    const firstSupplier = selectedQueueDetails[0].supplierId;
    const firstAccNo = selectedQueueDetails[0].bankAccount.accountNumber.replace(/\s+/g, '');
    return selectedQueueDetails.every(
      (q) => q.supplierId === firstSupplier && q.bankAccount.accountNumber.replace(/\s+/g, '') === firstAccNo
    );
  }, [selectedQueueDetails]);

  const handleOpenDisburseModal = () => {
    if (selectedQueueIds.length === 0) {
      toast.error('Pilih setidaknya 1 tiket antrian bayar');
      return;
    }
    if (!canConsolidateSelection) {
      toast.error('Pengajuan yang dicairkan dalam 1 voucher harus memiliki Supplier dan Rekening Bank tujuan yang sama!');
      return;
    }
    setTransferRef(`TRF-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(100 + Math.random() * 900)}`);
    setDisburseNotes('');
    setDisburseModalOpen(true);
  };

  const handleSubmitDisburse = async () => {
    if (!sourceBank.trim()) {
      toast.error('Pilih rekening bank sumber perusahaan');
      return;
    }

    try {
      setSubmittingDisburse(true);
      const res = await fetch('/api/finance/disburse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestIds: selectedQueueIds,
          sourceBank,
          transferRef,
          notes: disburseNotes,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menerbitkan Payment Voucher');
      }

      toast.success(json.message);
      setDisburseModalOpen(false);
      setSelectedQueueIds([]);
      fetchAllFinanceData();

      // Open detail of created voucher
      if (json.data) {
        setSelectedVoucherDetail(json.data);
        setVoucherDetailModalOpen(true);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal mencairkan voucher');
    } finally {
      setSubmittingDisburse(false);
    }
  };

  return (
    <div style={{ width: '100%', paddingBottom: 60 }}>
      {/* Header Banner */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 4 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: '#0369A1',
              background: '#E0F2FE',
              border: '1px solid #BAE6FD',
              padding: '2px 8px',
              borderRadius: 6,
              display: 'inline-block',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Accounts Payable & Pengeluaran Kas
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title
              level={2}
              style={{
                margin: 0,
                fontWeight: 800,
                color: '#0F172A',
                letterSpacing: '-0.02em',
                fontSize: 'clamp(20px, 4.5vw, 26px)',
              }}
            >
              Finance & Tagihan Vendor
            </Title>
            <Text style={{ color: '#64748B', fontSize: 13, marginTop: 4, display: 'block', maxWidth: 720 }}>
              Verifikasi kredibilitas 3-Way Match PO selesai, otoritas persetujuan nominal per termin oleh Finance Manager, dan antrian pencairan konsolidasi multi-PO ke 1 rekening tujuan.
            </Text>
          </div>

          <Space size="small">
            <Button
              icon={<ReloadOutlined style={{ fontSize: 13 }} />}
              onClick={fetchAllFinanceData}
              loading={loading}
              style={{ borderRadius: 8, height: 36, padding: '0 14px' }}
            >
              Segarkan
            </Button>
          </Space>
        </div>
      </div>

      {/* KPI Summary Metric Cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid #F1F5F9',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
              SISA HUTANG TAGIHAN
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: totalOutstandingVolume > 0 ? '#DC2626' : '#15803D', marginTop: 4 }}>
              {formatRupiah(totalOutstandingVolume)}
            </div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
              {unpaidPOs.length > 0 ? `${unpaidPOs.length} PO Belum Lunas` : 'Semua PO Selesai Lunas'}
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid #FEF3C7',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#B45309', textTransform: 'uppercase' }}>
              REVIEW TERMIN MANAGER
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#B45309', marginTop: 4 }}>
              {pendingManagerApprovalCount} Tiket
            </div>
            <div style={{ fontSize: 11, color: '#D97706', marginTop: 2 }}>
              Menunggu Otoritas Pencairan
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid #DCFCE7',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
              SIAP DICAIRKAN (KASIR)
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#15803D', marginTop: 4 }}>
              Rp {(queueDisbursementTotal / 1000000).toFixed(1)} jt
            </div>
            <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2 }}>
              {queueItems.length} Tiket Disetujui di Antrian
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase' }}>
              KAS SUDAH DIBAYAR
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#4338CA', marginTop: 4 }}>
              Rp {(totalPaidVolume / 1000000).toFixed(1)} jt
            </div>
            <div style={{ fontSize: 11, color: '#6366F1', marginTop: 2 }}>
              {vouchers.length} Payment Voucher Terbit
            </div>
          </div>
        </Col>
      </Row>

      {/* Main Workspace Card */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 16,
          boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.04)',
          border: '1px solid #F1F5F9',
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'pos',
              label: (
                <span>
                  <CheckCircleOutlined /> Tagihan PO Selesai ({unpaidPOs.length})
                </span>
              ),
            },
            {
              key: 'approvals',
              label: (
                <span>
                  <ClockCircleOutlined /> Persetujuan Manager ({paymentRequests.filter((r) => r.status === 'PENDING_APPROVAL').length})
                </span>
              ),
            },
            {
              key: 'queue',
              label: (
                <span>
                  <BankOutlined /> Antrian Bayar ({queueItems.length})
                </span>
              ),
            },
            {
              key: 'vouchers',
              label: (
                <span>
                  <AuditOutlined /> Riwayat Pembayaran ({vouchers.length})
                </span>
              ),
            },
          ]}
        />

        {/* ── TAB 1: COMPLETED POs LIST FOR FINANCE REVIEW ── */}
        {activeTab === 'pos' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Daftar tagihan PO selesai yang belum lunas untuk verifikasi dan pengajuan termin.
              </Text>
            </div>

            {unpaidPOs.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: '#64748B', fontSize: 13 }}>
                      Semua tagihan PO telah lunas
                    </span>
                  }
                >
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => setActiveTab('vouchers')}
                    style={{ borderRadius: 6, fontSize: 12, background: '#4F46E5', borderColor: '#4F46E5' }}
                  >
                    Riwayat Pembayaran ➔
                  </Button>
                </Empty>
              </div>
            ) : (
              <Table
                rowKey="id"
                dataSource={unpaidPOs}
                loading={loading}
                scroll={{ x: 1000 }}
                pagination={{ pageSize: 8 }}
              columns={[
                {
                  title: 'No. PO',
                  key: 'poNumber',
                  width: 170,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <Link
                      href={`/purchase-orders/${r.id}`}
                      style={{ fontWeight: 700, color: '#4F46E5', textDecoration: 'none' }}
                    >
                      {r.poNumber}
                    </Link>
                  ),
                },
                {
                  title: 'Supplier & Rekening Tujuan',
                  key: 'supplier',
                  width: 240,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <div>
                      <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{r.supplier.name}</div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {r.bankAccountSnapshot.bankName}: <strong>{r.bankAccountSnapshot.accountNumber}</strong>
                      </div>
                      <div style={{ fontSize: 10.5, color: '#94A3B8' }}>a.n. {r.bankAccountSnapshot.accountHolderName}</div>
                    </div>
                  ),
                },
                {
                  title: '3-Way Match Gudang',
                  key: 'match',
                  width: 170,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <div>
                      <Tag color="cyan" style={{ fontSize: 11, fontWeight: 700, borderRadius: 4 }}>
                        {r.threeWayMatch.totalGoodReceived} / {r.threeWayMatch.totalOrdered} Unit Tiba
                      </Tag>
                      {r.threeWayMatch.hasQualityRejections && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>
                          ⚠ Ada {r.threeWayMatch.totalRejectedAtGate} Unit Cacat/Reject
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Nilai PO',
                  key: 'totalAmount',
                  width: 140,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <Text strong style={{ fontSize: 13, color: '#0F172A' }}>
                      {formatRupiah(r.totalAmount)}
                    </Text>
                  ),
                },
                {
                  title: 'Status Bayar & Sisa Saldo',
                  key: 'balance',
                  width: 180,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <div>
                      <Tag
                        color={r.paymentStatus === 'PAID' ? 'success' : r.paymentStatus === 'PARTIALLY_PAID' ? 'warning' : 'default'}
                        style={{ fontWeight: 700, borderRadius: 4 }}
                      >
                        {r.paymentStatus === 'PAID' ? 'LUNAS (PAID)' : r.paymentStatus === 'PARTIALLY_PAID' ? 'DICICIL (PARTIAL)' : 'BELUM DIBAYAR'}
                      </Tag>
                      <div style={{ fontSize: 11.5, color: r.outstandingBalance > 0 ? '#B45309' : '#15803D', marginTop: 3 }}>
                        Sisa: <strong>{formatRupiah(r.outstandingBalance)}</strong>
                      </div>
                      {r.pendingAmount > 0 && (
                        <div style={{ fontSize: 10.5, color: '#6366F1' }}>
                          (Tiket Diajukan: {formatRupiah(r.pendingAmount)})
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Aksi',
                  key: 'actions',
                  width: 160,
                  render: (_: unknown, r: CompletedPOItem) => (
                    <Space size="small">
                      {r.availableToRequest > 0 ? (
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => handleOpenCreateTranche(r)}
                          style={{
                            background: '#0284C7',
                            borderColor: '#0284C7',
                            borderRadius: 6,
                            fontWeight: 600,
                          }}
                        >
                          Ajukan Termin
                        </Button>
                      ) : r.paymentStatus === 'PAID' ? (
                        <Button
                          size="small"
                          icon={<AuditOutlined />}
                          onClick={() => setActiveTab('vouchers')}
                          style={{
                            borderRadius: 6,
                            fontSize: 11.5,
                            color: '#15803D',
                            borderColor: '#BBF7D0',
                            background: '#F0FDF4',
                            fontWeight: 600,
                          }}
                        >
                          Lihat di Riwayat ↗
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          disabled
                          style={{ borderRadius: 6, fontSize: 11.5 }}
                        >
                          Termin Diajukan
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
            )}
          </div>
        )}

        {/* ── TAB 2: MANAGER APPROVAL QUEUE ── */}
        {activeTab === 'approvals' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Pengajuan termin yang membutuhkan persetujuan Finance Manager.
              </Text>
            </div>

            {pendingRequests.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: '#64748B', fontSize: 13 }}>
                      Tidak ada pengajuan yang perlu disetujui
                    </span>
                  }
                />
              </div>
            ) : (
              <Table
                rowKey="id"
                dataSource={pendingRequests}
                loading={loading}
                scroll={{ x: 1000 }}
                pagination={{ pageSize: 8 }}
              columns={[
                {
                  title: 'No. Tiket & Tanggal',
                  key: 'requestNumber',
                  width: 180,
                  render: (_: unknown, r: PaymentRequestItem) => (
                    <div>
                      <span style={{ fontWeight: 700, color: '#4F46E5', fontSize: 13 }}>{r.requestNumber}</span>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {new Date(r.createdAt).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'PO & Supplier',
                  key: 'po',
                  width: 220,
                  render: (_: unknown, r: PaymentRequestItem) => (
                    <div>
                      <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{r.po.poNumber}</div>
                      <div style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>{r.po.supplier.name}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>Inv: <strong>{r.vendorInvoiceNo}</strong></div>
                    </div>
                  ),
                },
                {
                  title: 'Termin & Nominal',
                  key: 'amount',
                  width: 200,
                  render: (_: unknown, r: PaymentRequestItem) => (
                    <div>
                      <Tag color="purple" style={{ fontWeight: 600, borderRadius: 4 }}>{r.trancheTitle}</Tag>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#0F172A', marginTop: 4 }}>
                        {formatRupiah(r.requestedAmount)}
                      </div>
                      {r.dueDate && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>
                          Jatuh Tempo: {new Date(r.dueDate).toLocaleDateString('id-ID')}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Status & Otoritas',
                  key: 'status',
                  width: 170,
                  render: (_: unknown, r: PaymentRequestItem) => (
                    <div>
                      <Tag
                        color={
                          r.status === 'PAID'
                            ? 'success'
                            : r.status === 'APPROVED'
                            ? 'blue'
                            : r.status === 'REJECTED'
                            ? 'error'
                            : 'warning'
                        }
                        style={{ fontWeight: 700, borderRadius: 4 }}
                      >
                        {r.status === 'PAID'
                          ? 'SUDAH CAIR (PAID)'
                          : r.status === 'APPROVED'
                          ? 'APPROVED (ANTRIAN)'
                          : r.status === 'REJECTED'
                          ? 'DITOLAK (REJECTED)'
                          : 'PENDING APPROVAL'}
                      </Tag>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>
                        Pemohon: {r.requestedBy.name}
                      </div>
                      {r.rejectionReason && (
                        <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2, fontStyle: 'italic' }}>
                          Alasan: &quot;{r.rejectionReason}&quot;
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Aksi Manager',
                  key: 'actions',
                  width: 170,
                  render: (_: unknown, r: PaymentRequestItem) => {
                    if (r.status !== 'PENDING_APPROVAL') {
                      return (
                        <span style={{ fontSize: 12, color: '#94A3B8' }}>
                          {r.status === 'APPROVED' ? '✓ Disetujui' : r.status === 'PAID' ? '✓ Telah Dicairkan' : '✕ Ditolak'}
                        </span>
                      );
                    }

                    return (
                      <Space size="small">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleApproveRequest(r.id)}
                          style={{
                            background: '#16A34A',
                            borderColor: '#16A34A',
                            borderRadius: 6,
                            fontWeight: 600,
                          }}
                        >
                          Setujui
                        </Button>
                        <Button
                          danger
                          size="small"
                          onClick={() => handleOpenRejectModal(r)}
                          style={{ borderRadius: 6 }}
                        >
                          Tolak
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
            />
            )}
          </div>
        )}

        {/* ── TAB 3: PAYMENT QUEUE & MULTI-PO CONSOLIDATION (FINANCE ADMIN) ── */}
        {activeTab === 'queue' && (
          <div>
            {queueItems.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: '#64748B', fontSize: 13 }}>
                      Tidak ada antrian pembayaran
                    </span>
                  }
                >
                  <Button
                    size="small"
                    onClick={() => setActiveTab('vouchers')}
                    style={{ borderRadius: 6, fontSize: 12 }}
                  >
                    Lihat Riwayat Pembayaran ➔
                  </Button>
                </Empty>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Tiket termin yang telah disetujui dan siap ditransfer kasir.
                    </Text>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      background: '#FFFFFF',
                      padding: '8px 16px',
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                    }}
                  >
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', letterSpacing: '0.04em' }}>
                        TOTAL TRANSFER TERPILIH
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: selectedQueueIds.length > 0 ? '#15803D' : '#64748B' }}>
                        {formatRupiah(selectedTransferTotal)}{' '}
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8' }}>
                          ({selectedQueueIds.length} PO)
                        </span>
                      </div>
                    </div>

                    <Button
                      type="primary"
                      icon={<BankOutlined style={{ fontSize: 15 }} />}
                      onClick={handleOpenDisburseModal}
                      disabled={selectedQueueIds.length === 0 || !canConsolidateSelection}
                      style={
                        selectedQueueIds.length === 0 || !canConsolidateSelection
                          ? {
                              background: '#F1F5F9',
                              color: '#94A3B8',
                              borderColor: '#E2E8F0',
                              borderRadius: 8,
                              height: 38,
                              fontWeight: 600,
                              cursor: 'not-allowed',
                              boxShadow: 'none',
                            }
                          : {
                              background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
                              borderColor: '#15803D',
                              color: '#FFFFFF',
                              borderRadius: 8,
                              height: 38,
                              fontWeight: 700,
                              padding: '0 20px',
                              boxShadow: '0 3px 10px rgba(22, 163, 74, 0.25)',
                            }
                      }
                    >
                      Cairkan Payment Voucher
                    </Button>
                  </div>
                </div>

                {!canConsolidateSelection && selectedQueueIds.length > 1 && (
                  <Alert
                    type="error"
                    showIcon
                    message="Peringatan Konsolidasi Rekening Berbeda"
                    description="Tiket yang Anda pilih memiliki Supplier atau nomor Rekening Bank yang berbeda. Pembayaran hanya dapat dikonsolidasikan untuk nomor rekening bank tujuan yang persis sama."
                    style={{ marginBottom: 16, borderRadius: 10 }}
                  />
                )}

                <Table
                  rowKey="id"
                  dataSource={queueItems}
                  loading={loading}
                  scroll={{ x: 1000 }}
                  pagination={{ pageSize: 8 }}
                  rowSelection={{
                    selectedRowKeys: selectedQueueIds,
                    onChange: (keys) => setSelectedQueueIds(keys as string[]),
                  }}
                  columns={[
                    {
                      title: 'No. Tiket',
                      dataIndex: 'requestNumber',
                      key: 'requestNumber',
                      width: 160,
                      render: (text) => <strong style={{ color: '#4F46E5' }}>{text}</strong>,
                    },
                    {
                      title: 'PO Terkait',
                      dataIndex: 'poNumber',
                      key: 'poNumber',
                      width: 150,
                      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
                    },
                    {
                      title: 'Supplier & Rekening Tujuan',
                      key: 'bank',
                      width: 260,
                      render: (_: unknown, r: any) => (
                        <div>
                          <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{r.supplierName}</div>
                          <div style={{ fontSize: 12, color: '#0369A1', marginTop: 2, fontWeight: 600 }}>
                            {r.bankAccount.bankName}: {r.bankAccount.accountNumber}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>a.n. {r.bankAccount.accountHolderName}</div>
                        </div>
                      ),
                    },
                    {
                      title: 'Termin & Nominal Siap Cair',
                      key: 'amount',
                      width: 200,
                      render: (_: unknown, r: any) => (
                        <div>
                          <Tag color="purple" style={{ fontWeight: 600, borderRadius: 4 }}>{r.trancheTitle}</Tag>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#15803D', marginTop: 4 }}>
                            {formatRupiah(r.requestedAmount)}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>Inv: {r.vendorInvoiceNo}</div>
                        </div>
                      ),
                    },
                    {
                      title: 'Disetujui Oleh',
                      key: 'approver',
                      width: 180,
                      render: (_: unknown, r: any) => (
                        <div>
                          <Tag color="blue" style={{ fontSize: 11, borderRadius: 4 }}>✓ Manager Approved</Tag>
                          <div style={{ fontSize: 11.5, color: '#475569', marginTop: 3 }}>
                            {r.reviewedBy}
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </div>
        )}

        {/* ── TAB 4: HISTORICAL PAYMENT VOUCHERS (PV ARCHIVE) ── */}
        {activeTab === 'vouchers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Riwayat dan arsip bukti kas keluar (Payment Voucher).
              </Text>
            </div>

            <Table
              rowKey="id"
              dataSource={vouchers}
              loading={loading}
              scroll={{ x: 1000 }}
              pagination={{ pageSize: 8 }}
              columns={[
                {
                  title: 'No. Voucher',
                  key: 'voucherNumber',
                  width: 180,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <div>
                      <a
                        onClick={() => {
                          setSelectedVoucherDetail(r);
                          setVoucherDetailModalOpen(true);
                        }}
                        style={{ fontWeight: 800, color: '#4F46E5', textDecoration: 'none', cursor: 'pointer' }}
                      >
                        {r.voucherNumber} ↗
                      </a>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        {new Date(r.paymentDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Supplier & Rekening Tujuan',
                  key: 'supplier',
                  width: 250,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <div>
                      <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 13 }}>{r.supplier.name}</div>
                      <div style={{ fontSize: 11.5, color: '#475569', marginTop: 2 }}>
                        {r.bankAccountSnapshot.bankName}: {r.bankAccountSnapshot.accountNumber}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Alokasi Multi-PO',
                  key: 'allocations',
                  width: 220,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <div>
                      <span style={{ fontWeight: 700, color: '#0F172A', fontSize: 12.5 }}>
                        {r.allocations.length} PO Terbayar
                      </span>
                      <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.allocations.map((a) => (
                          <Tag key={a.id} color="default" style={{ fontSize: 10, margin: 0 }}>
                            {a.po.poNumber}: Rp {(a.amount / 1000000).toFixed(1)}jt
                          </Tag>
                        ))}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Total Transfer',
                  key: 'total',
                  width: 160,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <div>
                      <Text strong style={{ fontSize: 15, color: '#15803D' }}>
                        {formatRupiah(r.totalAmount)}
                      </Text>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                        Ref: {r.transferRef || '-'}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Rekening Sumber Perusahaan',
                  key: 'source',
                  width: 220,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <div>
                      <div style={{ fontSize: 12, color: '#1E293B', fontWeight: 500 }}>{r.sourceBank}</div>
                      <div style={{ fontSize: 11, color: '#0369A1', marginTop: 2, fontWeight: 600 }}>
                        Dibayarkan: {r.disbursedBy.name}
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Aksi',
                  key: 'actions',
                  width: 130,
                  render: (_: unknown, r: PaymentVoucherItem) => (
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => {
                        setSelectedVoucherDetail(r);
                        setVoucherDetailModalOpen(true);
                      }}
                      style={{ borderRadius: 6, fontWeight: 500 }}
                    >
                      Slip PV
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Card>

      {/* ── MODAL 1: CREATE TRANCHE PAYMENT REQUEST ── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarOutlined style={{ color: '#0284C7' }} />
            <span>Pengajuan Termin Pencairan Kas (Finance Admin)</span>
          </div>
        }
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleSubmitTranche}
        confirmLoading={submittingTranche}
        okText="Kirim ke Finance Manager"
        cancelText="Batal"
        width={560}
      >
        {selectedPOForTranche && (
          <div style={{ marginTop: 12 }}>
            <div style={{ background: '#F8FAFC', padding: '12px 14px', borderRadius: 10, border: '1px solid #E2E8F0', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text strong style={{ color: '#0F172A' }}>{selectedPOForTranche.poNumber}</Text>
                <Tag color="cyan">{selectedPOForTranche.supplier.name}</Tag>
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>
                Total PO: <strong>{formatRupiah(selectedPOForTranche.totalAmount)}</strong> • Sisa Belum Diajukan: <strong style={{ color: '#0369A1' }}>{formatRupiah(selectedPOForTranche.availableToRequest)}</strong>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                NOMOR INVOICE VENDOR *
                <span style={{ fontSize: 11, fontWeight: 400, color: '#64748B', marginLeft: 6 }}>
                  (Nomor faktur/tagihan resmi yang tertera pada berkas fisik/PDF dari vendor)
                </span>
              </label>
              <Input
                placeholder="Contoh: INV-PJA-2026-089"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                style={{ borderRadius: 8, height: 38 }}
              />
            </div>

            <Row gutter={12} style={{ marginBottom: 14 }}>
              <Col span={12}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  TANGGAL INVOICE
                </label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  style={{ borderRadius: 8, height: 38 }}
                />
              </Col>
              <Col span={12}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  JATUH TEMPO
                </label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{ borderRadius: 8, height: 38 }}
                />
              </Col>
            </Row>

            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
                  NOMINAL YANG DIAJUKAN (RP) *
                </label>
                <Space size={4}>
                  <Button size="small" onClick={() => handleQuickPercent(30)} style={{ fontSize: 11, borderRadius: 4 }}>30%</Button>
                  <Button size="small" onClick={() => handleQuickPercent(50)} style={{ fontSize: 11, borderRadius: 4 }}>50%</Button>
                  <Button size="small" onClick={() => handleQuickPercent(100)} style={{ fontSize: 11, borderRadius: 4 }}>100% Lunas</Button>
                </Space>
              </div>
              <InputNumber
                style={{ width: '100%', borderRadius: 8, height: 38 }}
                min={1}
                max={selectedPOForTranche.availableToRequest}
                value={requestedAmount}
                onChange={(val) => setRequestedAmount(val || 0)}
                formatter={(val) => `Rp ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                parser={(val) => Number(val?.replace(/Rp\s?|(\.*)/g, '') || 0)}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                JUDUL / TAHAP TERMIN
              </label>
              <Input
                placeholder="Contoh: Termin 1 (DP 50%), Termin 2 (Pelunasan)"
                value={trancheTitle}
                onChange={(e) => setTrancheTitle(e.target.value)}
                style={{ borderRadius: 8, height: 38 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                CATATAN FINANCE ADMIN
              </label>
              <TextArea
                rows={2}
                placeholder="Instruksi tambahan atau referensi BAST surat jalan..."
                value={trancheNotes}
                onChange={(e) => setTrancheNotes(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ── MODAL 2: MANAGER REJECT REASON ── */}
      <Modal
        title="Tolak Pengajuan Termin Pembayaran"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleSubmitReject}
        confirmLoading={submittingReject}
        okText="Tolak Pengajuan"
        okButtonProps={{ danger: true }}
        cancelText="Batal"
      >
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13, color: '#334155' }}>
            Berikan alasan penolakan untuk pengajuan <strong>{requestToReject?.requestNumber}</strong> ({requestToReject?.po.poNumber}):
          </p>
          <TextArea
            rows={3}
            placeholder="Contoh: Faktur pajak belum lengkap, cashflow minggu ini dialihkan ke bahan baku prioritas..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </Modal>

      {/* ── MODAL 3: KASIR DISBURSE PAYMENT VOUCHER ── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BankOutlined style={{ color: '#16A34A' }} />
            <span>Pencairan Kas & Penerbitan Payment Voucher</span>
          </div>
        }
        open={disburseModalOpen}
        onCancel={() => setDisburseModalOpen(false)}
        onOk={handleSubmitDisburse}
        confirmLoading={submittingDisburse}
        okText="Terbitkan Voucher & Cairkan"
        okButtonProps={{ style: { background: '#16A34A', borderColor: '#16A34A', fontWeight: 700 } }}
        cancelText="Batal"
        width={620}
      >
        <div style={{ marginTop: 12 }}>
          {selectedQueueDetails.length > 0 && (
            <div style={{ background: '#F0FDF4', padding: '14px 16px', borderRadius: 10, border: '1px solid #BBF7D0', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
                REKENING TUJUAN VENDOR (SNAPSHOTTED)
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', marginTop: 4 }}>
                {selectedQueueDetails[0].bankAccount.bankName} — {selectedQueueDetails[0].bankAccount.accountNumber}
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>
                Atas Nama: <strong>{selectedQueueDetails[0].bankAccount.accountHolderName}</strong> ({selectedQueueDetails[0].supplierName})
              </div>
              <Divider style={{ margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>Total Transfer Gabungan:</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#15803D' }}>
                  {formatRupiah(selectedTransferTotal)}
                </span>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
              REKENING BANK SUMBER PERUSAHAAN *
            </label>
            <Select
              value={sourceBank}
              onChange={setSourceBank}
              style={{ width: '100%', height: 38 }}
              options={[
                { label: '🏦 BCA PT XYZ Operasional (Rek: 883-019-2831)', value: 'BCA PT XYZ Operasional (883-019-2831)' },
                { label: '🏦 Mandiri PT XYZ Rekening Giro (Rek: 124-000-8899123)', value: 'Mandiri PT XYZ Rekening Giro (124-000-8899123)' },
                { label: '💵 Kas Kecil / Petty Cash Operasional', value: 'Kas Kecil / Petty Cash Operasional' },
              ]}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
              NOMOR REFERENSI MUTASI TRANSFER BANK
            </label>
            <Input
              value={transferRef}
              onChange={(e) => setTransferRef(e.target.value)}
              placeholder="Contoh: TRF-BCA-20261013-0982"
              style={{ borderRadius: 8, height: 38 }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
              CATATAN PENCAIRAN KASIR
            </label>
            <TextArea
              rows={2}
              value={disburseNotes}
              onChange={(e) => setDisburseNotes(e.target.value)}
              placeholder="Keterangan tambahan mutasi transfer bank..."
              style={{ borderRadius: 8 }}
            />
          </div>
        </div>
      </Modal>

      {/* ── MODAL 4: VOUCHER DETAIL & PRINTABLE VIEW ── */}
      <Modal
        open={voucherDetailModalOpen}
        onCancel={() => setVoucherDetailModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setVoucherDetailModalOpen(false)}>
            Tutup
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => window.print()}
            style={{ background: '#4F46E5', borderColor: '#4F46E5', fontWeight: 600 }}
          >
            Cetak Dokumen
          </Button>,
        ]}
        width={700}
      >
        {selectedVoucherDetail && (
          <div className="printable-document" style={{ padding: '8px 4px' }}>
            <div style={{ textAlign: 'center', borderBottom: '2px solid #0F172A', paddingBottom: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#4F46E5', letterSpacing: '0.04em' }}>
                PT XYZ ENTERPRISE ERP
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginTop: 2 }}>
                BUKTI PENGELUARAN KAS / PAYMENT VOUCHER
              </div>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                Nomor: <strong>{selectedVoucherDetail.voucherNumber}</strong> • Tanggal: {new Date(selectedVoucherDetail.paymentDate).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            </div>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <div style={{ background: '#F8FAFC', padding: 12, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                    DIBAYARKAN KEPADA (VENDOR)
                  </div>
                  <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 14, marginTop: 2 }}>
                    {selectedVoucherDetail.supplier.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
                    {selectedVoucherDetail.bankAccountSnapshot.bankName}: <strong>{selectedVoucherDetail.bankAccountSnapshot.accountNumber}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748B' }}>
                    a.n. {selectedVoucherDetail.bankAccountSnapshot.accountHolderName}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ background: '#F8FAFC', padding: 12, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                    SUMBER DANA PERUSAHAAN
                  </div>
                  <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 13, marginTop: 2 }}>
                    {selectedVoucherDetail.sourceBank}
                  </div>
                  <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
                    Ref Transfer: <strong>{selectedVoucherDetail.transferRef || '-'}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: '#0369A1', marginTop: 2, fontWeight: 600 }}>
                    Dibayarkan Oleh (Finance Admin): {selectedVoucherDetail.disbursedBy.name}
                  </div>
                </div>
              </Col>
            </Row>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>
                RINCIAN ALOKASI PEMBAYARAN PO
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #CBD5E1', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>No. PO</th>
                    <th style={{ padding: '8px 10px' }}>Keterangan Termin</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Nominal Alokasi</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedVoucherDetail.allocations.map((a) => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0F172A' }}>{a.po.poNumber}</td>
                      <td style={{ padding: '8px 10px', color: '#475569' }}>{a.notes || '-'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#0F172A' }}>
                        {formatRupiah(a.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#F8FAFC', borderTop: '2px solid #0F172A' }}>
                    <td colSpan={2} style={{ padding: '10px', fontWeight: 800, fontSize: 13, textAlign: 'right' }}>
                      TOTAL PENCAIRAN KAS:
                    </td>
                    <td style={{ padding: '10px', fontWeight: 800, fontSize: 15, textAlign: 'right', color: '#15803D' }}>
                      {formatRupiah(selectedVoucherDetail.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Row gutter={16} style={{ marginTop: 24, textAlign: 'center' }}>
              <Col span={8}>
                <DigitalSignatureBox
                  titlePrefix="Dibuat Oleh:"
                  name={Array.from(new Set(selectedVoucherDetail.paymentRequests?.map((r: any) => r.requestedBy?.name).filter(Boolean))).join(', ') || 'Dewi Lestari'}
                  role="Finance Admin"
                  date={new Date(selectedVoucherDetail.paymentDate).toLocaleDateString('id-ID')}
                />
              </Col>
              <Col span={8}>
                <DigitalSignatureBox
                  titlePrefix="Disetujui:"
                  name={Array.from(new Set(selectedVoucherDetail.paymentRequests?.map((r: any) => r.reviewedBy?.name).filter(Boolean))).join(', ') || 'Hendra Wijaya'}
                  role="Finance Manager"
                  date={new Date(selectedVoucherDetail.paymentDate).toLocaleDateString('id-ID')}
                />
              </Col>
              <Col span={8}>
                <DigitalSignatureBox
                  titlePrefix="Dibayarkan Kasir:"
                  name={selectedVoucherDetail.disbursedBy?.name || 'Kasir / Finance Admin'}
                  role="Kasir / Finance Admin"
                  date={new Date(selectedVoucherDetail.paymentDate).toLocaleDateString('id-ID')}
                />
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
}
